import React, { useEffect, useRef, useState } from "react";

/**
 * ScaleToFit — renders children at a fixed design resolution and scales the
 * whole block with one CSS transform to fit its container (nothing reflows,
 * nothing clips). Used for the LANDSCAPE/desktop slide only; portrait is handled
 * by rendering a separate mobile layout (see useIsPortrait + HTLDSlide*Mobile).
 */

export const DESIGN_W = 1280;
export const DESIGN_H = 720;

interface ScaleToFitProps {
  children: React.ReactNode;
  designWidth?: number;
  designHeight?: number;
  background?: string;
}

const ScaleToFit: React.FC<ScaleToFitProps> = ({
  children,
  designWidth = DESIGN_W,
  designHeight = DESIGN_H,
  background = "#040f18",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) setScale(Math.min(width / designWidth, height / designHeight));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [designWidth, designHeight]);

  return (
    <div ref={containerRef}
      style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background }}>
      <div style={{ width: designWidth, height: designHeight, flexShrink: 0, transform: `scale(${scale})`, transformOrigin: "center center" }}>
        {children}
      </div>
    </div>
  );
};

export default ScaleToFit;
